/**
 * Runs `worker` over `items` with at most `concurrency` calls in flight at once, instead of
 * firing every call simultaneously (an unbounded `Promise.all`) or one at a time.
 */
export async function mapWithConcurrency<T, R>(
    items: T[],
    concurrency: number,
    worker: (item: T) => Promise<R>,
    onProgress?: (completed: number, total: number) => void,
): Promise<R[]> {
    if (items.length === 0) {
        return [];
    }

    const results: R[] = new Array(items.length);
    let nextIndex = 0;
    let completed = 0;
    const workerCount = Math.max(1, Math.min(concurrency, items.length));

    const run = async (): Promise<void> => {
        while (nextIndex < items.length) {
            const index = nextIndex;
            nextIndex += 1;
            results[index] = await worker(items[index]);
            completed += 1;
            onProgress?.(completed, items.length);
        }
    };

    await Promise.all(Array.from({ length: workerCount }, () => run()));
    return results;
}
